/**
 * CompleteProfileScreen — Écran de complétion du profil utilisateur
 *
 * Accessible depuis Profil > "Modifier mon profil" quand l'user a des infos
 * manquantes (cas typique : Sign in with Apple avec masquage email/nom).
 *
 * Champs demandés :
 *   - Prénom + Nom (toujours, si manquants)
 *   - Genre (toujours, si manquant)
 *   - Date de naissance (optionnel)
 *   - Email (UNIQUEMENT si manquant ou alias Apple privaterelay)
 *
 * Style Outfit "Soir de Paris" : charbon chaud + dorures champagne
 *  - Bouton retour ← en haut à gauche
 *  - Titre centré
 *  - Adapte clair/sombre via useTheme
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, TextInput,
  SafeAreaView, StatusBar, Alert, ActivityIndicator, KeyboardAvoidingView,
  Platform, Modal, useWindowDimensions,
} from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { isAppleRelayEmail } from '../utils/userDisplay';
import { silhouetteOptions, sizeOptions, estimateSizes } from '../utils/morphoHelper';

// Import dynamique de DateTimePicker — fallback gracieux si la lib n'est pas
// installée (vieille build, dev sans rebuild). Dans ce cas, fallback sur
// une saisie texte AAAA-MM-JJ.
// v1.12.117 — sur web, on FORCE null car le picker RN ne marche pas
// correctement (button qui ouvre un picker invisible). On utilise un
// <input type="date"> HTML natif via WebDateInput à la place.
let DateTimePicker = null;
if (Platform.OS !== 'web') {
  try {
    DateTimePicker = require('@react-native-community/datetimepicker').default;
  } catch {
    DateTimePicker = null;
  }
}
import WebDateInput from '../components/WebDateInput';

const GOLD      = '#D4AF72';
const GOLD_DARK = '#B8860B';

// v1.12.157 — Age minimum 15 ans (RGPD France, LIL art.45). Avant : 13 ans.
function getMaxDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 15);
  return d;
}
// Helper : date la plus ancienne acceptée (110 ans aujourd'hui)
function getMinDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 110);
  return d;
}
// Helper : date par défaut quand aucune sélection (30 ans en arrière)
function getDefaultDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 30);
  return d;
}
// Helper : convertit Date JS → "AAAA-MM-JJ"
function formatDateISO(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
// Helper : affichage user-friendly (ex: "12 mars 1992")
const MONTHS_FR = ['janvier','février','mars','avril','mai','juin',
  'juillet','août','septembre','octobre','novembre','décembre'];
function formatDateFR(isoString) {
  if (!isoString) return '';
  const m = String(isoString).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return isoString;
  return `${parseInt(m[3], 10)} ${MONTHS_FR[parseInt(m[2], 10) - 1]} ${m[1]}`;
}
// Helper : normalise toute date (string ISO, "YYYY-MM-DDTHH:mm:ss.sssZ",
// objet Date) → "YYYY-MM-DD". Utilisé pour homogénéiser ce qui vient de la DB
// (pg-node peut retourner un Date object ou une string ISO complète selon le
// driver) avec ce que le calendrier produit (formatDateISO direct).
function toIsoDateOnly(value) {
  if (!value) return '';
  // Déjà au bon format ?
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  // Sinon parser en Date
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return formatDateISO(d);
}

const getColors = (dark) => ({
  bg:        dark ? '#0E0C0A' : '#FAFAFA',
  card:      dark ? '#1A1814' : '#FFFFFF',
  text:      dark ? '#EEEEEE' : '#1A1814',
  textMuted: dark ? '#A89E94' : '#7A7268',
  textFaint: dark ? '#666666' : '#A89E94',
  border:    dark ? '#2A2622' : '#EAE6E0',
  inputBg:   dark ? '#1A1814' : '#FFFFFF',
  gold:      GOLD,
  goldDark:  dark ? '#D4B896' : GOLD_DARK,
  goldLight: dark ? '#332A20' : '#FFF7E0',
  red:       '#E53935',
});

export default function CompleteProfileScreen({ navigation }) {
  const { user, updateUserProfile, getValidToken } = useAuth();
  const { dark } = useTheme();
  const { t, lang } = useI18n();
  const { height: winHeight } = useWindowDimensions();
  const COLORS = getColors(dark);

  // Détection des champs manquants à demander
  // FIX 2026-04-26 : `emailIsMissing` doit être recalculé après le fetch direct
  // /profile (cas où user.email est null au mount mais arrive depuis la DB).
  // Sinon le champ "Email valide requis" reste rouge à tort.
  // → On évalue à partir de l'email réellement disponible (user OU state local
  // après hydratation depuis le fetch direct DB).
  const initialEmailMissing = !user?.email || isAppleRelayEmail(user.email);

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName,  setLastName]  = useState(user?.lastName || '');
  const [email,     setEmail]     = useState(initialEmailMissing ? '' : (user?.email || ''));

  // emailIsMissing dynamique : prend en compte le state local `email` (qui sera
  // rempli par le fetch /profile direct ci-dessous) → l'utilisateur ne voit le
  // champ "Email valide requis" que si l'email est VRAIMENT manquant.
  const emailIsMissing = (() => {
    const eff = (email || user?.email || '').trim();
    if (!eff) return true;
    if (isAppleRelayEmail(eff)) return true;
    return false;
  })();
  const [gender,    setGender]    = useState(user?.gender || '');
  const [birthDate, setBirthDate] = useState(toIsoDateOnly(user?.birthDate));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempDate, setTempDate] = useState(null); // pour iOS spinner avant validation

  // ═══ Morpho/style ═══
  const [heightCm,   setHeightCm]   = useState(user?.height_cm ? String(user.height_cm) : '');
  const [weightKg,   setWeightKg]   = useState(user?.weight_kg ? String(user.weight_kg) : '');
  const [silhouette, setSilhouette] = useState(user?.silhouette || '');
  const initSizes = user?.clothing_sizes || {};
  const [sizeTops,    setSizeTops]    = useState(initSizes.tops    || '');
  const [sizeBottoms, setSizeBottoms] = useState(initSizes.bottoms || '');
  const [sizeShoes,   setSizeShoes]   = useState(initSizes.shoes   || '');
  const [sizeJacket,  setSizeJacket]  = useState(initSizes.jacket  || '');

  const [saving,    setSaving]    = useState(false);

  // ═══ Pré-remplir avec les valeurs DB dès qu'elles arrivent ═══
  // useState(initial) ne fonctionne qu'au premier mount. Si user.profileHydrated
  // n'est pas encore true au mount, les champs sont vides. Cet effet recopie
  // les valeurs DB dans les states quand elles deviennent disponibles, mais
  // SEULEMENT si l'user n'a pas déjà tapé dedans.
  useEffect(() => {
    if (!user) return;
    if (!firstName && user.firstName) setFirstName(user.firstName);
    if (!lastName  && user.lastName)  setLastName(user.lastName);
    if (!gender    && user.gender)    setGender(user.gender);
    if (!birthDate && user.birthDate) setBirthDate(toIsoDateOnly(user.birthDate));
    if (!email && user.email && !isAppleRelayEmail(user.email)) setEmail(user.email);
    if (!heightCm  && user.height_cm) setHeightCm(String(user.height_cm));
    if (!weightKg  && user.weight_kg) setWeightKg(String(user.weight_kg));
    if (!silhouette && user.silhouette) setSilhouette(user.silhouette);
    const cs = user.clothing_sizes || {};
    if (!sizeTops    && cs.tops)    setSizeTops(cs.tops);
    if (!sizeBottoms && cs.bottoms) setSizeBottoms(cs.bottoms);
    if (!sizeShoes   && cs.shoes)   setSizeShoes(cs.shoes);
    if (!sizeJacket  && cs.jacket)  setSizeJacket(cs.jacket);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.firstName, user?.lastName, user?.gender, user?.birthDate, user?.email,
      user?.height_cm, user?.weight_kg, user?.silhouette, user?.clothing_sizes]);

  // ═══ Fetch direct /profile au mount pour garantir le pré-remplissage ═══
  // Sécurité ceinture-bretelles : même si l'AuthContext n'a pas (encore) hydraté
  // user avec les champs DB (gender, birth_date, morpho, sizes), on fait un fetch
  // dédié ici. Comme ça l'écran "Modifier mon profil" affiche TOUJOURS les valeurs
  // sauvegardées en DB, peu importe l'état du contexte.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const accessToken = user?.accessToken;
      if (!accessToken) return;
      try {
        const { AWS_CONFIG } = require('../config/aws_config');
        const res = await fetch(`${AWS_CONFIG.apiUrl}/profile`, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
        if (!res.ok) return;
        const prof = await res.json();
        if (cancelled || !prof) return;

        // Patcher uniquement les champs vides (ne pas écraser ce que l'user
        // est en train de taper). On utilise une fonction helper pour rester
        // safe avec les states React.
        const patchIfEmpty = (current, dbValue, setter) => {
          if (!current && dbValue) setter(String(dbValue));
        };
        patchIfEmpty(firstName, prof.first_name, setFirstName);
        patchIfEmpty(lastName,  prof.last_name,  setLastName);
        patchIfEmpty(gender,    prof.gender,     setGender);
        // Normaliser birth_date : DB peut renvoyer 'YYYY-MM-DDTHH:mm:ss.sssZ'
        // (Date object JSON-stringifié) → on extrait juste 'YYYY-MM-DD' pour
        // que la validation regex passe.
        if (!birthDate && prof.birth_date) setBirthDate(toIsoDateOnly(prof.birth_date));
        // FIX 2026-04-26 : toujours patcher email depuis DB s'il existe.
        // Avant : seulement si !emailIsMissing au mount, donc les users dont
        // user.email était null au mount ne récupéraient jamais l'email DB →
        // champ "Email valide requis" reste rouge même quand la DB a la valeur.
        // Maintenant emailIsMissing est dynamique, donc patcher est safe.
        if (prof.email && !isAppleRelayEmail(prof.email)) {
          patchIfEmpty(email, prof.email, setEmail);
        }
        if (prof.height_cm)  patchIfEmpty(heightCm, prof.height_cm, setHeightCm);
        if (prof.weight_kg)  patchIfEmpty(weightKg, prof.weight_kg, setWeightKg);
        patchIfEmpty(silhouette, prof.silhouette, setSilhouette);
        const cs = prof.clothing_sizes || {};
        patchIfEmpty(sizeTops,    cs.tops,    setSizeTops);
        patchIfEmpty(sizeBottoms, cs.bottoms, setSizeBottoms);
        patchIfEmpty(sizeShoes,   cs.shoes,   setSizeShoes);
        patchIfEmpty(sizeJacket,  cs.jacket,  setSizeJacket);

        // Synchroniser aussi le AuthContext pour que le reste de l'app voie
        // ces champs (ProfileScreen affichera correctement le badge gender, etc.)
        // FIX 2026-04-26 : sync aussi firstName/lastName/email/name/avatar — sans ça,
        // après plusieurs login successifs, le ProfileScreen affiche un email
        // vide / le nom mal résolu parce qu'AuthContext garde les anciens champs
        // alors que la DB en a déjà de meilleurs.
        const ctxPatch = {};
        if (!user.firstName && prof.first_name) ctxPatch.firstName = prof.first_name;
        if (!user.lastName  && prof.last_name)  ctxPatch.lastName  = prof.last_name;
        if (!user.name      && prof.name)       ctxPatch.name      = prof.name;
        if ((!user.email || isAppleRelayEmail(user.email)) && prof.email && !isAppleRelayEmail(prof.email)) {
          ctxPatch.email = prof.email;
        }
        if (!user.avatar && prof.avatar_url)    ctxPatch.avatar    = prof.avatar_url;
        if (!user.gender    && prof.gender)     ctxPatch.gender     = prof.gender;
        if (!user.birthDate && prof.birth_date) ctxPatch.birthDate  = prof.birth_date;
        if (!user.height_cm && prof.height_cm)  ctxPatch.height_cm  = Number(prof.height_cm);
        if (!user.weight_kg && prof.weight_kg)  ctxPatch.weight_kg  = Number(prof.weight_kg);
        if (!user.silhouette && prof.silhouette) ctxPatch.silhouette = prof.silhouette;
        if (!user.clothing_sizes && prof.clothing_sizes) ctxPatch.clothing_sizes = prof.clothing_sizes;
        if (Object.keys(ctxPatch).length > 0) {
          ctxPatch.profileHydrated = true;
          updateUserProfile(ctxPatch);
        }

        console.log('[CompleteProfile] ✓ DB fetch direct: gender=', prof.gender,
          'birthDate=', prof.birth_date, 'height=', prof.height_cm, 'weight=', prof.weight_kg);
      } catch (e) {
        console.warn('[CompleteProfile] direct fetch failed (non bloquant):', e?.message);
      }
    })();
    return () => { cancelled = true; };
    // Volontairement pas de deps : on fetch UNE fois au mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-suggestion des tailles quand taille/poids changent et tailles vides
  useEffect(() => {
    const h = parseInt(heightCm, 10);
    const w = parseInt(weightKg, 10);
    if (!h || !w || !gender) return;
    if (sizeTops && sizeBottoms && sizeShoes && sizeJacket) return;
    const est = estimateSizes(h, w, gender);
    if (!sizeTops    && est.tops)    setSizeTops(est.tops);
    if (!sizeBottoms && est.bottoms) setSizeBottoms(est.bottoms);
    if (!sizeShoes   && est.shoes)   setSizeShoes(est.shoes);
    if (!sizeJacket  && est.jacket)  setSizeJacket(est.jacket);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightCm, weightKg, gender]);

  const styles = useMemo(() => makeStyles(COLORS), [dark]);

  // Validation
  const errors = {};
  if (!firstName || firstName.trim().length < 2) errors.firstName = 'Au moins 2 caractères';
  if (!lastName  || lastName.trim().length  < 2) errors.lastName  = 'Au moins 2 caractères';
  if (!gender) errors.gender = 'Sélectionnez votre genre';
  if (emailIsMissing) {
    if (!email || !email.includes('@')) errors.email = 'Email valide requis';
  }
  // Date obligatoire + format + plage réaliste (13-100 ans)
  if (!birthDate) {
    errors.birthDate = 'Requis pour personnaliser vos suggestions';
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
    errors.birthDate = 'Format : AAAA-MM-JJ';
  } else {
    const d = new Date(birthDate);
    const today = new Date();
    const ageYears = (today - d) / (365.25 * 24 * 3600 * 1000);
    // v1.12.157 — Age minimum 15 ans (RGPD France, LIL art.45)
    if (isNaN(ageYears) || ageYears < 15) {
      errors.birthDate = 'Vous devez avoir au moins 15 ans';
    } else if (ageYears > 110) {
      errors.birthDate = 'Date invalide';
    }
  }
  // ═══ Détection des modifications ═══
  // Le bouton "Enregistrer" est dégrisé seulement si l'user a modifié au moins
  // une valeur par rapport à la DB. Évite les saves inutiles.
  const sameAsDb = (a, b) => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return String(a).trim() === String(b).trim();
  };
  const sameSizes = (a, b) => {
    const ka = Object.keys(a || {}); const kb = Object.keys(b || {});
    if (ka.length !== kb.length) return false;
    return ka.every(k => sameAsDb(a[k], b[k]));
  };
  const currentSizes = {
    ...(sizeTops    ? { tops:    sizeTops }    : {}),
    ...(sizeBottoms ? { bottoms: sizeBottoms } : {}),
    ...(sizeShoes   ? { shoes:   sizeShoes }   : {}),
    ...(sizeJacket  ? { jacket:  sizeJacket }  : {}),
  };
  const hasChanges =
    !sameAsDb(firstName,  user?.firstName)        ||
    !sameAsDb(lastName,   user?.lastName)         ||
    (emailIsMissing && email && !sameAsDb(email, user?.email)) ||
    !sameAsDb(gender,     user?.gender)           ||
    !sameAsDb(birthDate,  user?.birthDate)        ||
    !sameAsDb(heightCm ? parseInt(heightCm, 10) : null, user?.height_cm) ||
    !sameAsDb(weightKg ? parseInt(weightKg, 10) : null, user?.weight_kg) ||
    !sameAsDb(silhouette, user?.silhouette)       ||
    !sameSizes(currentSizes, user?.clothing_sizes || {});

  const canSave = Object.keys(errors).length === 0 && !saving && hasChanges;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // Snake_case pour le backend (la convention de l'API)
      const payload = {
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        name:       `${firstName.trim()} ${lastName.trim()}`.trim(),
        gender,
        birth_date: birthDate || null,
      };
      // Email : seulement si demandé (sinon on ne touche pas à l'existant)
      if (emailIsMissing && email && email.includes('@')) {
        payload.email = email.trim().toLowerCase();
      }
      // Morpho/style — envoyés uniquement s'ils ont une valeur
      if (heightCm)  payload.height_cm = parseInt(heightCm, 10);
      if (weightKg)  payload.weight_kg = parseInt(weightKg, 10);
      if (silhouette) payload.silhouette = silhouette;
      if (sizeTops || sizeBottoms || sizeShoes || sizeJacket) {
        payload.clothing_sizes = {
          ...(sizeTops    ? { tops:    sizeTops }    : {}),
          ...(sizeBottoms ? { bottoms: sizeBottoms } : {}),
          ...(sizeShoes   ? { shoes:   sizeShoes }   : {}),
          ...(sizeJacket  ? { jacket:  sizeJacket }  : {}),
        };
      }

      // Mise à jour locale du user (en camelCase pour le contexte React)
      const localUpdates = {
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        name:      `${firstName.trim()} ${lastName.trim()}`.trim(),
        gender,
        birthDate: birthDate || null,
        ...(payload.email ? { email: payload.email } : {}),
        ...(payload.height_cm ? { height_cm: payload.height_cm } : {}),
        ...(payload.weight_kg ? { weight_kg: payload.weight_kg } : {}),
        ...(payload.silhouette ? { silhouette: payload.silhouette } : {}),
        ...(payload.clothing_sizes ? { clothing_sizes: payload.clothing_sizes } : {}),
      };

      // Appel backend direct (snake_case) puis mise à jour du contexte
      // v1.12.926 — utiliser getValidToken() (auto-refresh) au lieu de user.accessToken
      // figé (peut être expiré après 1h → 401 Unauthorized).
      const { AWS_CONFIG } = require('../config/aws_config');
      const accessToken = (typeof getValidToken === 'function')
        ? await getValidToken()
        : user?.accessToken;
      if (accessToken) {
        const res = await fetch(`${AWS_CONFIG.apiUrl}/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`HTTP ${res.status}: ${txt.slice(0, 100)}`);
        }
      }
      await updateUserProfile(localUpdates);

      Alert.alert('✓ Profil mis à jour', 'Vos informations ont été enregistrées.', [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    } catch (e) {
      console.warn('[CompleteProfile] save failed:', e.message);
      Alert.alert('Erreur', `Impossible d'enregistrer.\n\n${e.message || 'Réessayez plus tard.'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.safe}>
      <StatusBar barStyle={dark ? 'light-content' : 'dark-content'} />

      {/* Header avec bouton retour */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backBtn}
          activeOpacity={0.6}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Text style={styles.backIcon}>‹</Text>
        </TouchableOpacity>
        <Text style={styles.title}>{t('edit_profile_title')}</Text>
        <View style={{ width: 40 }} />
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={[styles.content, { paddingBottom: 140 }]}
          keyboardShouldPersistTaps="handled"
          style={Platform.OS === 'web' ? { height: winHeight - 100, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : undefined}
        >
          {/* Intro */}
          <View style={styles.introCard}>
            <Text style={styles.introBadge}>{t('personalization_caps')}</Text>
            <Text style={styles.introTitle}>{t('your_information')}</Text>
            <Text style={styles.introSub}>
              {t('info_helps_personalization')}
            </Text>
          </View>

          {/* Prénom */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('first_name')}</Text>
            <TextInput
              style={[styles.input, errors.firstName && styles.inputError]}
              value={firstName}
              onChangeText={setFirstName}
              placeholder="Votre prénom"
              placeholderTextColor={COLORS.textFaint}
              autoCapitalize="words"
              autoCorrect={false}
            />
            {errors.firstName ? <Text style={styles.error}>{errors.firstName}</Text> : null}
          </View>

          {/* Nom */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('last_name')}</Text>
            <TextInput
              style={[styles.input, errors.lastName && styles.inputError]}
              value={lastName}
              onChangeText={setLastName}
              placeholder="Votre nom"
              placeholderTextColor={COLORS.textFaint}
              autoCapitalize="words"
              autoCorrect={false}
            />
            {errors.lastName ? <Text style={styles.error}>{errors.lastName}</Text> : null}
          </View>

          {/* Email — seulement si manquant ou alias Apple */}
          {emailIsMissing && (
            <View style={styles.field}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.hint}>
                {user?.email && isAppleRelayEmail(user.email)
                  ? 'Apple a masqué votre email. Saisissez celui que vous souhaitez utiliser pour Outfit.'
                  : 'Saisissez votre adresse email.'}
              </Text>
              <TextInput
                style={[styles.input, errors.email && styles.inputError]}
                value={email}
                onChangeText={setEmail}
                placeholder="vous@exemple.com"
                placeholderTextColor={COLORS.textFaint}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
              />
              {errors.email ? <Text style={styles.error}>{errors.email}</Text> : null}
            </View>
          )}

          {/* Genre */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('gender')}</Text>
            <View style={styles.genderRow}>
              {[
                { value: 'F', label: t('gender_woman') },
                { value: 'M', label: t('gender_man') },
              ].map(opt => (
                <TouchableOpacity
                  key={opt.value}
                  onPress={() => setGender(opt.value)}
                  style={[
                    styles.genderBtn,
                    gender === opt.value && styles.genderBtnActive,
                  ]}
                  activeOpacity={0.85}
                >
                  <Text style={[
                    styles.genderBtnText,
                    gender === opt.value && styles.genderBtnTextActive,
                  ]}>
                    {opt.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {errors.gender ? <Text style={styles.error}>{errors.gender}</Text> : null}
          </View>

          {/* Date de naissance — obligatoire pour personnaliser les suggestions */}
          <View style={styles.field}>
            <Text style={styles.label}>{t('date_of_birth')}</Text>
            <Text style={styles.hint}>
              {t('age_adapts_styles')}
            </Text>

            {Platform.OS === 'web' ? (
              <WebDateInput
                value={birthDate}
                onChange={setBirthDate}
                min={formatDateISO(getMinDate())}
                max={formatDateISO(getMaxDate())}
                hasError={!!errors.birthDate}
                lang={lang}
              />
            ) : DateTimePicker ? (
              <>
                {/* Bouton qui affiche la date sélectionnée et ouvre le picker natif */}
                <TouchableOpacity
                  style={[styles.dateBtn, errors.birthDate && styles.inputError]}
                  onPress={() => {
                    // v1.13.61 — Android : API IMPÉRATIVE (le composant déclaratif
                    // se réinitialisait à la date initiale → saisie impossible).
                    if (Platform.OS === 'android') {
                      try {
                        const { DateTimePickerAndroid } = require('@react-native-community/datetimepicker');
                        DateTimePickerAndroid.open({
                          value: birthDate ? new Date(birthDate) : getDefaultDate(),
                          mode: 'date',
                          minimumDate: getMinDate(),
                          maximumDate: getMaxDate(),
                          onChange: (event, d) => {
                            if (event?.type !== 'dismissed' && d) setBirthDate(formatDateISO(d));
                          },
                        });
                      } catch (e) { console.warn('[CompleteProfile] DateTimePickerAndroid:', e?.message); }
                      return;
                    }
                    // iOS : modale spinner
                    setTempDate(birthDate ? new Date(birthDate) : getDefaultDate());
                    setShowDatePicker(true);
                  }}
                  activeOpacity={0.85}
                >
                  <Text style={[
                    styles.dateBtnText,
                    !birthDate && { color: COLORS.textFaint },
                  ]}>
                    {birthDate ? formatDateFR(birthDate) : 'Sélectionner ma date de naissance'}
                  </Text>
                  <Text style={styles.dateBtnIcon}>📅</Text>
                </TouchableOpacity>

                {/* v1.13.61 — Android : plus de picker déclaratif (remplacé par
                    DateTimePickerAndroid.open impératif dans le onPress). */}

                {/* Picker iOS — modale custom slide-up avec backdrop opaque */}
                {Platform.OS === 'ios' && (
                  <Modal
                    visible={showDatePicker}
                    transparent
                    animationType="slide"
                    onRequestClose={() => setShowDatePicker(false)}
                    statusBarTranslucent
                  >
                    {/* Backdrop : tap pour fermer */}
                    <TouchableOpacity
                      activeOpacity={1}
                      style={styles.iosModalBg}
                      onPress={() => setShowDatePicker(false)}
                    >
                      {/* Sheet : on absorbe le tap pour ne pas fermer en tapant dessus */}
                      <TouchableOpacity
                        activeOpacity={1}
                        onPress={() => {}}
                        style={styles.iosModalSheet}
                      >
                        {/* Handle visuel */}
                        <View style={styles.iosModalHandle} />
                        <View style={styles.iosModalHeader}>
                          <TouchableOpacity onPress={() => setShowDatePicker(false)} style={styles.iosModalBtn}>
                            <Text style={styles.iosModalCancel}>Annuler</Text>
                          </TouchableOpacity>
                          <Text style={styles.iosModalTitle}>Date de naissance</Text>
                          <TouchableOpacity onPress={() => {
                            if (tempDate) setBirthDate(formatDateISO(tempDate));
                            setShowDatePicker(false);
                          }} style={styles.iosModalBtn}>
                            <Text style={styles.iosModalDone}>Valider</Text>
                          </TouchableOpacity>
                        </View>
                        <View style={styles.iosPickerBox}>
                          <DateTimePicker
                            value={tempDate || (birthDate ? new Date(birthDate) : getDefaultDate())}
                            mode="date"
                            display="spinner"
                            minimumDate={getMinDate()}
                            maximumDate={getMaxDate()}
                            locale={lang === 'en' ? 'en-US' : 'fr-FR'}
                            textColor={COLORS.text}
                            themeVariant={dark ? 'dark' : 'light'}
                            onChange={(event, selectedDate) => {
                              if (selectedDate) setTempDate(selectedDate);
                            }}
                            style={styles.iosPicker}
                          />
                        </View>
                      </TouchableOpacity>
                    </TouchableOpacity>
                  </Modal>
                )}
              </>
            ) : (
              // ═══ Fallback : si DateTimePicker n'est pas installé, saisie texte ═══
              <TextInput
                style={[styles.input, errors.birthDate && styles.inputError]}
                value={birthDate}
                onChangeText={setBirthDate}
                placeholder="AAAA-MM-JJ"
                placeholderTextColor={COLORS.textFaint}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="numbers-and-punctuation"
                maxLength={10}
              />
            )}

            {errors.birthDate ? <Text style={styles.error}>{errors.birthDate}</Text> : null}
          </View>

          {/* ═══ Section morphologie ═══ */}
          <View style={styles.sectionDivider} />
          <Text style={styles.sectionTitle}>{t('morphology')}</Text>
          <Text style={styles.sectionSub}>
            {t('morphology_explanation')}
          </Text>

          <View style={styles.fieldRow}>
            <View style={[styles.field, { flex: 1, marginRight: 8 }]}>
              <Text style={styles.label}>{t('height_cm')}</Text>
              <TextInput
                style={styles.input}
                value={heightCm}
                onChangeText={t => setHeightCm(t.replace(/\D/g, '').slice(0, 3))}
                placeholder="175"
                placeholderTextColor={COLORS.textFaint}
                keyboardType="number-pad"
                maxLength={3}
              />
            </View>
            <View style={[styles.field, { flex: 1, marginLeft: 8 }]}>
              <Text style={styles.label}>{t('weight_kg')}</Text>
              <TextInput
                style={styles.input}
                value={weightKg}
                onChangeText={t => setWeightKg(t.replace(/\D/g, '').slice(0, 3))}
                placeholder="70"
                placeholderTextColor={COLORS.textFaint}
                keyboardType="number-pad"
                maxLength={3}
              />
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>{t('silhouette')}</Text>
            <View style={{ gap: 8 }}>
              {silhouetteOptions(gender || 'F', t).map(opt => {
                const active = silhouette === opt.value;
                return (
                  <TouchableOpacity
                    key={opt.value}
                    onPress={() => setSilhouette(opt.value)}
                    style={[styles.silCard, active && styles.silCardActive]}
                    activeOpacity={0.85}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.silCardLabel, active && styles.silCardLabelActive]}>
                        {opt.label}
                      </Text>
                      {opt.hint ? (
                        <Text style={[styles.silCardHint, active && styles.silCardHintActive]}>
                          {opt.hint}
                        </Text>
                      ) : null}
                    </View>
                    <View style={[styles.silCardDot, active && styles.silCardDotActive]} />
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* ═══ Section tailles ═══ */}
          <View style={styles.sectionDivider} />
          <Text style={styles.sectionTitle}>{t('clothing_sizes')}</Text>
          <Text style={styles.sectionSub}>
            {t('sizes_prefilled_from_morphology')}
          </Text>

          <View style={styles.field}>
            <Text style={styles.label}>{t('tops_label_with_examples')}</Text>
            <View style={styles.sizesRow}>
              {sizeOptions('tops', gender || 'F').map(v => (
                <TouchableOpacity key={v} onPress={() => setSizeTops(v)}
                  style={[styles.sizeChip, sizeTops === v && styles.sizeChipActive]}
                  activeOpacity={0.85}>
                  <Text style={[styles.sizeChipText, sizeTops === v && styles.sizeChipTextActive]}>{v}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>{t('bottoms_label_with_examples_eu')}</Text>
            <View style={styles.sizesRow}>
              {sizeOptions('bottoms', gender || 'F').map(v => (
                <TouchableOpacity key={v} onPress={() => setSizeBottoms(v)}
                  style={[styles.sizeChip, sizeBottoms === v && styles.sizeChipActive]}
                  activeOpacity={0.85}>
                  <Text style={[styles.sizeChipText, sizeBottoms === v && styles.sizeChipTextActive]}>{v}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>{t('jacket_coat_label')}</Text>
            <View style={styles.sizesRow}>
              {sizeOptions('jacket', gender || 'F').map(v => (
                <TouchableOpacity key={v} onPress={() => setSizeJacket(v)}
                  style={[styles.sizeChip, sizeJacket === v && styles.sizeChipActive]}
                  activeOpacity={0.85}>
                  <Text style={[styles.sizeChipText, sizeJacket === v && styles.sizeChipTextActive]}>{v}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>{t('shoes_eu_label')}</Text>
            <View style={styles.sizesRow}>
              {sizeOptions('shoes', gender || 'F').map(v => (
                <TouchableOpacity key={v} onPress={() => setSizeShoes(v)}
                  style={[styles.sizeChip, sizeShoes === v && styles.sizeChipActive]}
                  activeOpacity={0.85}>
                  <Text style={[styles.sizeChipText, sizeShoes === v && styles.sizeChipTextActive]}>{v}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Bouton enregistrer */}
          <TouchableOpacity
            style={[styles.saveBtn, !canSave && styles.saveBtnDisabled]}
            onPress={handleSave}
            disabled={!canSave}
            activeOpacity={0.88}
          >
            {saving ? (
              <ActivityIndicator color="#0E0C0A" />
            ) : (
              <Text style={[styles.saveBtnText, !canSave && styles.saveBtnTextDisabled]}>
                {!hasChanges
                  ? t('no_changes').toUpperCase()
                  : (Object.keys(errors).length > 0 ? t('fix_errors').toUpperCase() : t('save').toUpperCase())}
              </Text>
            )}
          </TouchableOpacity>

          <Text style={styles.footer}>
            {t('profile_privacy_notice') || 'Vos informations sont confidentielles et utilisées uniquement pour personnaliser votre expérience Mytenue.'}
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const makeStyles = (C) => StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: C.bg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    // ═══ Compense la status bar sur Android ═══
    // Sur iOS, SafeAreaView gère l'encoche. Sur Android, il faut ajouter
    // manuellement la hauteur de la status bar (24-30px selon device).
    paddingTop: Platform.OS === 'android'
      ? (StatusBar.currentHeight || 24) + 8
      : 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: C.border,
  },
  backBtn: {
    // Cercle subtil cohérent avec le design "Soir de Paris" :
    // fond très léger qui contraste juste assez avec le bg, bordure 1px
    // de la même teinte que les autres séparateurs de l'app.
    width: 38, height: 38,
    borderRadius: 19,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: C.bgRaised || C.bgSurface || '#F5F4F0',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: C.bgBorder || C.border,
  },
  backIcon: {
    // Chevron fin et élégant ‹ plutôt que la flèche pleine ←.
    // fontSize plus grand car le chevron est visuellement plus petit que →.
    fontSize: 28,
    color: C.text,
    fontWeight: '300',
    // Léger offset pour centrer optiquement le chevron (qui pousse à droite par défaut)
    marginLeft: -2,
    marginTop: -2,
  },
  title: {
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: 0.3,
    color: C.text,
  },
  content: {
    padding: 20, paddingBottom: 40,
  },
  introCard: {
    marginBottom: 24,
    paddingHorizontal: 4,
  },
  introBadge: {
    fontSize: 9,
    letterSpacing: 3,
    color: C.goldDark,
    fontWeight: '700',
    marginBottom: 8,
  },
  introTitle: {
    fontSize: 24,
    fontWeight: '300',
    color: C.text,
    letterSpacing: 0.3,
    marginBottom: 8,
  },
  introSub: {
    fontSize: 13,
    color: C.textMuted,
    lineHeight: 19,
  },
  field: {
    marginBottom: 18,
  },
  fieldRow: {
    flexDirection: 'row', alignItems: 'flex-start',
  },
  sectionDivider: {
    height: 1, backgroundColor: C.border, marginVertical: 18,
  },
  sectionTitle: {
    fontSize: 16, fontWeight: '700', color: C.text,
    letterSpacing: -0.2, marginBottom: 4,
  },
  sectionSub: {
    fontSize: 12, color: C.textMuted, marginBottom: 16, lineHeight: 17,
  },
  hintInline: {
    fontSize: 9, color: C.textFaint, fontWeight: '400',
    letterSpacing: 0, textTransform: 'none',
  },
  chipsRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 8,
  },
  chip: {
    paddingHorizontal: 14, paddingVertical: 8,
    borderRadius: 20, borderWidth: 1.5, borderColor: C.border,
    backgroundColor: C.inputBg,
  },
  chipActive: {
    borderColor: C.gold, backgroundColor: C.goldLight,
  },
  chipText: { fontSize: 13, color: C.text, fontWeight: '500' },
  chipTextActive: { color: C.goldDark, fontWeight: '700' },
  sizesRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 6,
  },
  sizeChip: {
    minWidth: 48, paddingHorizontal: 8, paddingVertical: 7,
    borderRadius: 8, borderWidth: 1.5, borderColor: C.border,
    backgroundColor: C.inputBg, alignItems: 'center',
  },
  sizeChipActive: {
    borderColor: C.gold, backgroundColor: C.goldLight,
  },
  sizeChipText: { fontSize: 12, color: C.text, fontWeight: '600' },
  sizeChipTextActive: { color: C.goldDark, fontWeight: '700' },

  // Silhouette card avec hint descriptif
  silCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 14, paddingVertical: 12,
    borderRadius: 10, borderWidth: 1.5, borderColor: C.border,
    backgroundColor: C.inputBg,
  },
  silCardActive: { borderColor: C.gold, backgroundColor: C.goldLight },
  silCardLabel: { fontSize: 14, fontWeight: '600', color: C.text, marginBottom: 2 },
  silCardLabelActive: { color: C.goldDark, fontWeight: '700' },
  silCardHint: { fontSize: 11, color: C.textMuted, lineHeight: 15 },
  silCardHintActive: { color: C.goldDark, opacity: 0.85 },
  silCardDot: {
    width: 16, height: 16, borderRadius: 8,
    borderWidth: 2, borderColor: C.border, backgroundColor: 'transparent',
  },
  silCardDotActive: {
    borderColor: C.goldDark, backgroundColor: C.goldDark,
  },
  label: {
    fontSize: 11,
    letterSpacing: 1.5,
    color: C.textMuted,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  optional: {
    color: C.textFaint,
    fontWeight: '400',
    letterSpacing: 0,
    textTransform: 'none',
    fontSize: 10,
  },
  hint: {
    fontSize: 12,
    color: C.textMuted,
    marginBottom: 8,
    lineHeight: 17,
    fontStyle: 'italic',
  },
  input: {
    borderWidth: 1,
    borderColor: C.border,
    backgroundColor: C.inputBg,
    color: C.text,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 10,
    fontSize: 15,
  },
  inputError: {
    borderColor: C.red,
  },

  // ═══ Bouton date picker ═══
  dateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: C.border,
    backgroundColor: C.inputBg,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 10,
  },
  dateBtnText: {
    fontSize: 15,
    color: C.text,
    fontWeight: '500',
  },
  dateBtnIcon: {
    fontSize: 18,
  },

  // ═══ Modal iOS pour date picker ═══
  iosModalBg: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'flex-end',
  },
  iosModalSheet: {
    // FIX 2026-04-26 : C.surface n'existait pas → fond transparent. Utiliser C.card.
    backgroundColor: C.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 28,
    // Ombre haute pour bien dissocier du fond
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 16,
  },
  iosModalHandle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.border,
    marginTop: 8,
    marginBottom: 4,
  },
  iosModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: C.border,
  },
  iosModalTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: C.text,
    letterSpacing: 0.3,
  },
  iosModalBtn: {
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  iosModalCancel: {
    fontSize: 15,
    color: C.textMuted,
  },
  iosModalDone: {
    fontSize: 15,
    fontWeight: '700',
    color: C.goldDark,
  },
  // Box autour du picker iOS — fond solide pour éviter la confusion visuelle
  iosPickerBox: {
    // FIX 2026-04-26 : C.surface inexistant → C.card (fond solide opaque)
    backgroundColor: C.card,
    paddingTop: 8,
    paddingHorizontal: 12,
  },
  iosPicker: {
    // FIX 2026-04-26 : C.surface inexistant → C.card. iOS rend le spinner
    // en mode "transparent" par défaut sur certaines versions, le fond
    // explicite garantit la lisibilité des chiffres.
    backgroundColor: C.card,
    height: 220,
  },
  error: {
    fontSize: 11,
    color: C.red,
    marginTop: 4,
    marginLeft: 2,
  },
  genderRow: {
    flexDirection: 'row',
    gap: 8,
  },
  genderBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: C.border,
    backgroundColor: C.inputBg,
    alignItems: 'center',
  },
  genderBtnActive: {
    borderColor: C.gold,
    backgroundColor: C.goldLight,
  },
  genderBtnText: {
    fontSize: 14,
    color: C.textMuted,
    fontWeight: '500',
  },
  genderBtnTextActive: {
    color: C.goldDark,
    fontWeight: '700',
  },
  saveBtn: {
    marginTop: 16,
    backgroundColor: C.gold,
    paddingVertical: 16,
    borderRadius: 100,
    alignItems: 'center',
  },
  saveBtnDisabled: {
    opacity: 0.4,
  },
  saveBtnText: {
    color: '#0E0C0A',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 1.5,
  },
  saveBtnTextDisabled: {
    color: '#0E0C0A',
  },
  footer: {
    marginTop: 20,
    fontSize: 11,
    color: C.textFaint,
    textAlign: 'center',
    lineHeight: 16,
  },
});
